import React from "react";
import ReactDOM from "react-dom";

const MENU_W = 236;
const ITEM_H = 40;
const EDGE = 8;

/**
 * Kebab (⋮) action menu for a table row.
 *
 * The menu is portalled to <body> and positioned with fixed coordinates:
 * the table lives inside `.hm-tbl-wrap`, which scrolls on the x axis, and any
 * absolutely positioned dropdown inside it would be clipped by that overflow.
 *
 * `items` is an array of { key, label, icon, tone, disabled, onSelect }. A disabled item is
 * rendered greyed rather than dropped, so a read-only viewer can still see which actions exist
 * on a row and that they are simply not theirs to use.
 */
function RowMenu({ label, items }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const shown = (items != null ? items : []).filter(Boolean);

  const place = React.useCallback(() => {
    const btn = btnRef.current;
    if (btn == null) return;
    const rect = btn.getBoundingClientRect();
    const height = shown.length * ITEM_H + 12;
    const left = Math.max(
      EDGE,
      Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - EDGE)
    );
    const below = rect.bottom + 6;
    const flip = below + height > window.innerHeight - EDGE && rect.top - height - 6 > EDGE;
    setPos({ top: flip ? rect.top - height - 6 : below, left });
  }, [shown.length]);

  React.useEffect(() => {
    if (!open) return undefined;

    place();

    const onDocDown = (e) => {
      if (btnRef.current != null && btnRef.current.contains(e.target)) return;
      if (menuRef.current != null && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        if (btnRef.current != null) btnRef.current.focus();
      }
    };
    // Any scroll — page or the table's own horizontal scroller — moves the
    // trigger out from under a fixed-position menu, so just close it.
    const onScroll = () => setOpen(false);

    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, place]);

  const handleSelect = (item) => {
    if (item.disabled === true) return;
    setOpen(false);
    if (item.onSelect != null) item.onSelect();
  };

  if (shown.length === 0) {
    return <span className="hm-rowmenu-none">—</span>;
  }

  return (
    <>
      <button
        ref={btnRef}
        className={"hm-kebab" + (open ? " is-open" : "")}
        type="button"
        aria-label={label != null ? label : "Row actions"}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span aria-hidden={true}>⋮</span>
      </button>

      {open && pos != null && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="hm-menu"
          role="menu"
          style={{ top: pos.top, left: pos.left, width: MENU_W }}
        >
          {shown.map((item) => (
            <button
              className={"hm-menu-i" + (item.tone === "danger" ? " is-danger" : "")}
              type="button"
              role="menuitem"
              key={item.key}
              disabled={item.disabled === true}
              aria-disabled={item.disabled === true}
              onClick={() => handleSelect(item)}
            >
              {item.icon && <i className={item.icon} aria-hidden="true"></i>}
              <span>{item.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default RowMenu;
